import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  IconButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Text,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";
import { useFormikContext } from "formik";
import moment from "moment";
import React from "react";
import useConfirmation from "../../hooks/useConfirmation";
import useQuote from "../../hooks/useQuote";
import useUser from "../../hooks/useUser";
import { darkMode, lightMode } from "../../lib/models/color-mode-colors";
import { formatDateLocal, formatDateTime } from "../../lib/number-transforms";
import { QuoteUtils } from "../../lib/quote-utils";
import { QuoteFormActions } from "../../lib/types/QuoteFormActions";
import { isElevatedUser } from "../../lib/UserPermissions";
import DeleteQuoteDialog from "../DeleteQuoteDialog";
import { AnnotationIcon } from "../icons/AnnotationIcon";
import { ClockIcon } from "../icons/ClockIcon.tsx";
import DotsHorizontalIcon from "../icons/DotsHorizontalIcon";
import { ExclamationTriangleIcon } from "../icons/ExclamationTriangleIcon";
import { UserIcon } from "../icons/UserIcon";
import FormRateLockStatus from "./FormRateLockStatus";
import FormSubmittedStatus from "./FormSubmittedStatus";

interface FormQuoteSummaryProps {
  handler: (action: QuoteFormActions, values: any) => void;
}

function FormQuoteSummary({ handler }: FormQuoteSummaryProps) {
  const [isOpen, setIsOpen] = React.useState(true);
  const quoteContext = useQuote();
  const { role } = useUser();
  const { state, fetchQuote, isOwned } = quoteContext;
  const { values, isValid, dirty } = useFormikContext<any>();
  const { quote, metadata } = state;
  const bgColor = useColorModeValue(lightMode[100], darkMode[200]);
  const quoteHandler = (action: QuoteFormActions) => {
    handler(action, values);
  };
  const { dialogOpen, loading, toggleOpen, performAction } = useConfirmation(
    () => quoteHandler("DELETE")
  );

  const milestone = metadata?.milestoneUpdatedAt
    ? moment(
        metadata?.milestoneUpdatedAt.replace(" ", "T"),
        "YYYY-MM-DD hh:mm:ss"
      ).format("MM/DD/YY")
    : undefined;

  const commonDisable =
    !isValid ||
    !state.hasCalculated ||
    state.hasCalculationErrors ||
    state.requiresQuoteResubmission ||
    state.hasFormErrors;

  return (
    <Box
      background={bgColor}
      padding={4}
      borderRadius="md"
      boxShadow="sm"
      minW={275}
    >
      <DeleteQuoteDialog
        isOpen={dialogOpen}
        isQuoteDeleting={loading}
        onConfirmHandler={performAction}
        onCloseHandler={toggleOpen}
        quoteToDelete={null}
      />
      <Flex justifyContent="space-between" alignItems="center">
        <Heading size="sm">Quote Summary</Heading>

        {!state.isNewQuote && (
          <Menu>
            <MenuButton
              as={IconButton}
              title="More Options"
              colorScheme="brand"
              variant="ghost"
              aria-label="More Options"
              icon={<DotsHorizontalIcon h={6} w={6} />}
              width={1}
              size={"sm"}
              display={{ base: "none", lg: "block" }}
            ></MenuButton>
            <MenuList>
              <MenuItem
                isDisabled={commonDisable || dirty}
                onClick={() => fetchQuote()}
              >
                Reload Quote
              </MenuItem>
              <MenuItem
                isDisabled={commonDisable || dirty}
                onClick={() => quoteHandler("SAVE_AS")}
              >
                Copy Quote
              </MenuItem>
              {(isOwned || isElevatedUser(role)) && (
                <>
                  <MenuDivider />
                  <MenuItem
                    isDisabled={commonDisable || dirty}
                    textColor={"red.600"}
                    onClick={() => toggleOpen()}
                  >
                    Delete Quote
                  </MenuItem>
                </>
              )}
            </MenuList>
          </Menu>
        )}

        <Button
          display={{ base: "inline-flex", lg: "none" }}
          rightIcon={
            isOpen ? (
              <ChevronUpIcon height={6} width={6} />
            ) : (
              <ChevronDownIcon height={6} width={6} />
            )
          }
          onClick={() => setIsOpen(!isOpen)}
          variant="unstyled"
          size="sm"
        >
          {isOpen ? "Collapse" : "Expand"}
        </Button>
      </Flex>

      {isOpen && (
        <>
          <Divider paddingBottom={3} />
          <VStack
            width="full"
            spacing={6}
            alignItems="flex-start"
            marginTop={4}
          >
            <Box display="flex" flexDir="column" width="full">
              {state.quoteType && (
                <Box paddingBottom={2}>
                  <Flex alignItems="center" color="gray.500" fontWeight="light">
                    <AnnotationIcon mr={2} />
                    <Text fontSize="xs">Quote Type</Text>
                  </Flex>
                  <Text
                    fontWeight="medium"
                    fontSize="sm"
                    marginTop={1}
                    textAlign="left"
                  >
                    {QuoteUtils.PrettyPrintQuoteType(state.quoteType)}
                  </Text>
                </Box>
              )}

              {values.username && state.username !== quote?.username && (
                <Box paddingBottom={2}>
                  <Flex alignItems="center" color="gray.500" fontWeight="light">
                    <UserIcon mr={2} />
                    <Text fontSize="xs">Owned By</Text>
                  </Flex>
                  <Text
                    fontWeight="medium"
                    fontSize="sm"
                    marginTop={1}
                    textAlign="left"
                  >
                    {quote?.username}
                  </Text>
                </Box>
              )}

              {state.metadata?.createdDate && (
                <Box paddingBottom={2}>
                  <Flex alignItems="center" color="gray.500" fontWeight="light">
                    <ClockIcon mr={2} />
                    <Text fontSize="xs">Date Created</Text>
                  </Flex>
                  <Text
                    fontWeight="medium"
                    fontSize="sm"
                    marginTop={1}
                    textAlign="left"
                  >
                    {formatDateLocal(state.metadata.createdDate)}
                  </Text>
                </Box>
              )}

              {state.metadata?.updatedDate && (
                <Box paddingBottom={2}>
                  <Flex alignItems="center" color="gray.500" fontWeight="light">
                    <ClockIcon mr={2} />
                    <Text fontSize="xs">Last Updated</Text>
                  </Flex>
                  <Text
                    fontWeight="medium"
                    fontSize="sm"
                    marginTop={1}
                    textAlign="left"
                  >
                    {formatDateTime(state.metadata.updatedDate)}
                  </Text>
                </Box>
              )}

              {state.metadata?.exceptionStatus && (
                <Box paddingBottom={2}>
                  <Flex alignItems="center" color="gray.500" fontWeight="light">
                    <ExclamationTriangleIcon mr={2} />
                    <Text fontSize="xs">Exception Status</Text>
                  </Flex>
                  <Text
                    fontWeight="medium"
                    fontSize="sm"
                    marginTop={1}
                    textAlign="left"
                  >
                    <Badge>{state.metadata?.exceptionStatus}</Badge>
                  </Text>
                </Box>
              )}

              {state.hasQuoteBeenSubmitted && (
                <Box paddingBottom={2}>
                  <Flex alignItems="center" color="gray.500" fontWeight="light">
                    <ClockIcon mr={2} />
                    <Text fontSize="xs">Submission Status</Text>
                  </Flex>
                  <FormSubmittedStatus
                    requiresResubmission={state.requiresQuoteResubmission}
                    hasBeenSubmitted={state.hasQuoteBeenSubmitted}
                  />
                </Box>
              )}

              {metadata?.rateLock?.rateLockActive && (
                <FormRateLockStatus rateLock={metadata.rateLock} />
              )}

              {metadata?.milestone && (
                <Box paddingBottom={2}>
                  <Flex alignItems="center" color="gray.500" fontWeight="light">
                    <Text fontSize="xs">Milestone</Text>
                  </Flex>
                  <Badge colorScheme="gray">{metadata?.milestone}</Badge>
                </Box>
              )}

              {metadata?.milestoneUpdatedAt && (
                <Box paddingBottom={2}>
                  <Flex alignItems="center" color="gray.500" fontWeight="light">
                    <ClockIcon mr={2} />
                    <Text fontSize="xs">Milestone Last Updated</Text>
                  </Flex>
                  <Text
                    fontWeight="medium"
                    fontSize="sm"
                    marginTop={1}
                    textAlign="left"
                  >
                    {milestone}
                  </Text>
                </Box>
              )}
            </Box>
          </VStack>
        </>
      )}
    </Box>
  );
}

export default FormQuoteSummary;
